import { motion } from "framer-motion";
import { Play, Eye, ThumbsUp, Bookmark, BookmarkCheck } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { formatCount, timeAgo } from "@/lib/config";
import { isSaved, toggleSaved } from "@/lib/storage";
import { ShareMenu } from "@/components/ShareMenu";
import { RankMovement, VelocityBadge } from "@/components/RankMovement";

/**
 * Thumbnail card used by search, library, channel, leaderboard and compare.
 *
 * Deliberately lighter than the home-page card: no hover-autoplay, no audio context,
 * no scroll observers. Those pages show many results at once and the rich card's
 * per-card IntersectionObservers would be wasted work there.
 */
export const VideoCardCompact = ({ video, onPlay, showRank = false, linkTo }) => {
  const [saved, setSaved] = useState(() => isSaved(video.id));

  // Two ways to open a video: the modal (fast, keeps the visitor on the current
  // list) or a link to its own page (addressable, shareable, indexable). Callers
  // that pass linkTo get the second.
  const thumbnail = (
    <>
      <div className="aspect-video bg-black/40 overflow-hidden">
        <img
          src={video.thumbnail_url}
          alt={video.title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      </div>

      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
        <span className="w-11 h-11 rounded-full bg-orange-500 flex items-center justify-center">
          <Play className="w-5 h-5 text-black fill-black ml-0.5" />
        </span>
      </div>

      {showRank && video.trending_rank > 0 && (
        <span className="absolute top-2 right-2 flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-black/75 text-[11px] font-bold text-orange-400">
          #{video.trending_rank}
          <RankMovement video={video} />
        </span>
      )}

      {video.duration && video.duration !== "0:00" && (
        <span className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded bg-black/80 text-[10px] font-medium">
          {video.duration}
        </span>
      )}
    </>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="group rounded-xl overflow-hidden bg-white/[0.03] border border-white/10 hover:border-orange-500/40 transition-colors"
      data-testid={`compact-card-${video.id}`}
    >
      {linkTo ? (
        <Link to={linkTo(video)} className="relative block w-full text-left" aria-label={`Open ${video.title}`}>
          {thumbnail}
        </Link>
      ) : (
        <button onClick={() => onPlay?.(video)} className="relative block w-full text-left" aria-label={`Play ${video.title}`}>
          {thumbnail}
        </button>
      )}

      <div className="p-3 space-y-1.5">
        {linkTo ? (
          <Link to={linkTo(video)} className="block hover:text-orange-400 transition-colors">
            <h3 className="text-xs sm:text-sm font-bold leading-tight line-clamp-2">{video.title}</h3>
          </Link>
        ) : (
          <h3 className="text-xs sm:text-sm font-bold leading-tight line-clamp-2">{video.title}</h3>
        )}

        {video.channel_id ? (
          <Link
            to={`/channel/${video.channel_id}`}
            className="block text-[11px] text-white/50 hover:text-orange-400 truncate transition-colors"
          >
            {video.channel_title}
          </Link>
        ) : (
          <p className="text-[11px] text-white/50 truncate">{video.channel_title}</p>
        )}

        <div className="flex items-center gap-3 text-[10px] text-white/40">
          {(video.view_count_raw > 0 || video.view_count) && (
            <span className="inline-flex items-center gap-1">
              <Eye className="w-2.5 h-2.5" /> {formatCount(video.view_count_raw) || video.view_count}
            </span>
          )}
          {(video.like_count_raw > 0 || video.like_count) && (
            <span className="inline-flex items-center gap-1">
              <ThumbsUp className="w-2.5 h-2.5" /> {formatCount(video.like_count_raw) || video.like_count}
            </span>
          )}
          {video.published_at && <span>{timeAgo(video.published_at)}</span>}
        </div>

        <VelocityBadge video={video} />

        <div className="flex items-center justify-between pt-1">
          <button
            onClick={() => setSaved(toggleSaved(video))}
            className="inline-flex items-center gap-1 text-[11px] text-white/50 hover:text-orange-400 transition-colors"
            aria-label={saved ? "Remove from saved" : "Save video"}
          >
            {saved ? <BookmarkCheck className="w-3.5 h-3.5 text-orange-400" /> : <Bookmark className="w-3.5 h-3.5" />}
            {saved ? "Saved" : "Save"}
          </button>
          <ShareMenu video={video} compact />
        </div>
      </div>
    </motion.div>
  );
};

export const CompactGrid = ({ videos, onPlay, showRank = false, linkTo, emptyMessage = "Nothing here yet." }) => {
  if (!videos?.length) {
    return <p className="text-center text-sm text-white/40 py-16">{emptyMessage}</p>;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {videos.map((video) => (
        <VideoCardCompact
          key={video.id}
          video={video}
          onPlay={onPlay}
          showRank={showRank}
          linkTo={linkTo}
        />
      ))}
    </div>
  );
};
